import React, { MouseEventHandler } from "react";
import { useNavigate } from "react-router";

export const ButtonVariants = {
  primary:
    "text-white bg-blue-600 hover:bg-blue-700 focus:ring-4 focus:outline-none focus:ring-blue-300 dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800",
  secondary:
    "text-white bg-gray-600 hover:bg-gray-700 focus:ring-4 focus:outline-none focus:ring-gray-300 dark:bg-gray-600 dark:hover:bg-gray-700 dark:focus:ring-gray-800",
  danger:
    "text-white bg-red-600 hover:bg-red-700 focus:ring-4 focus:outline-none focus:ring-red-300 dark:bg-red-600 dark:hover:bg-red-700 dark:focus:ring-red-800",
  warning:
    "text-white bg-yellow-600 hover:bg-yellow-700 focus:ring-4 focus:outline-none focus:ring-yellow-300 dark:bg-yellow-600 dark:hover:bg-yellow-700 dark:focus:ring-yellow-800",
  success:
    "text-white bg-green-600 hover:bg-green-700 focus:ring-4 focus:outline-none focus:ring-green-300 dark:bg-green-600 dark:hover:bg-green-700 dark:focus:ring-green-800",
  info:
    "text-white bg-cyan-600 hover:bg-cyan-700 focus:ring-4 focus:outline-none focus:ring-cyan-300 dark:bg-cyan-600 dark:hover:bg-cyan-700 dark:focus:ring-cyan-800",
  default:
    "text-white bg-gray-600 hover:bg-gray-700 focus:ring-4 focus:outline-none focus:ring-gray-300 dark:bg-gray-600 dark:hover:bg-gray-700 dark:focus:ring-gray-800",
  outline:
    "text-gray-600 bg-transparent text-gray-600 border-2 border-gray-600 hover:bg-gray-600 hover:text-white focus:ring-4 focus:outline-none focus:ring-gray-300 dark:border-gray-400 dark:text-gray-400 dark:hover:bg-gray-400 dark:hover:text-gray-900 dark:focus:ring-gray-800",
};

const disabledStyle = "bg-gray-300 text-gray-500 cursor-not-allowed hover:bg-gray-300 hover:text-gray-500 focus:ring-gray-300 dark:bg-gray-700 dark:text-gray-400 dark:cursor-not-allowed dark:hover:bg-gray-700 dark:hover:text-gray-400 dark:focus:ring-gray-700";

interface AppButtonProps {
  variant: keyof typeof ButtonVariants; // Type for button styles
  type?: "button" | "submit" | "reset" | null; // Optional prop for button type
  disabled?: boolean; // Optional prop to disable the button
  onClick?: MouseEventHandler<HTMLButtonElement> | null; // Optional prop for click handler
  href?: string; // The URL to navigate to when the button is clicked
  inNewWindow?: boolean; // Optional prop to determine if the link should open in a new window
  backArrow?: boolean; // Optional prop to determine if the button should have a back arrow
  forwardArrow?: boolean; // Optional prop to determine if the button should have a forward arrow
  className?: string; // Optional className for additional styling
  children?: React.ReactNode;
}

/**
 * Renders a button that can either trigger a click event or navigate to a URL.
 * @param
 * @returns
 */
const AppButton: React.FC<AppButtonProps> = ({
  variant = "default",
  type = null,
  onClick = null,
  href = null,
  inNewWindow = false,
  backArrow = false,
  forwardArrow = false,
  disabled = false,
  className,
  children,
}) => {
  // ensure that either onClick or href is provided, but not both
  if (!onClick && !href && type !== "submit" && type !== "reset") {
    throw new Error("Either a type for use in forms must be provided, or onClick or href must be provided");
  }
  if (onClick && href) {
    throw new Error("Only one of onClick or href can be provided");
  }

  const navigate = useNavigate();

  const openHref = (href, inNewWindow) => {
    if (inNewWindow) {
      window.open(href, "_blank", "noopener,noreferrer");
    }
    else {
      navigate(href);
    }
  }

  return (
    <button
      type={type || (onClick ? "button" : "submit")}
      disabled={disabled}
      onClick={!disabled ? (onClick || (() => openHref(href!, inNewWindow))) : undefined}
      className={`cursor-pointer inline-flex items-center px-3 py-2 text-sm rounded-md font-medium text-center ${ButtonVariants[variant]} ${className} ${disabled ? disabledStyle : ""}`}
    >
      {backArrow && <span className="mr-2">←</span>}
      {children}
      {forwardArrow && <span className="ml-2">→</span>}
    </button>
  );
};

export default AppButton;
